import { isValidNumberForRegion } from './phone-validation/validator';
import { validateFullName } from './validators/validate-full-name';
import { I18n } from '../i18n';

import { Countries, ValidationErrors, IFormSubmitValues } from '../../types';

type CountryObjectRegexes<T extends string> = { [key in T]: RegExp };

const DEFAULT_PINCODE_REGEX = /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} ?\d{1}[A-Z]{1}\d{1}$)/i;

const EMAIL_REGEX = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;

const SPECIAL_CHARACTERS_REGEX = /\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\/|\""|\;|\:/gi;

const VALIDATION_CONSTANTS = {
  required: 'required',
  pdsError: 'pdsError',
  selectOfBusiness: 'selectOfBusiness',
  specialCharacters: 'specialCharacters',
  zipCode: 'zipcode',
  zipCodeFiveDigits: 'zipCodeFiveDigits',
  email: 'email',
  url: 'url',
  phone: 'phone',
  fullName: 'fullName',
};

const PINCODE_REGEXES: CountryObjectRegexes<Countries> = {
  US: /^\d{5}$/,
  CA: /^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/i,
  IE: /(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$/i,
  GB: /[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]? [0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}/i,
  NL: /[1-9][0-9]{3}/,
  BE: /[0-9]{4}/,
  PL: /[0-9]{2}\-[0-9]{3}/,
  FR: DEFAULT_PINCODE_REGEX,
  IT: DEFAULT_PINCODE_REGEX,
  ES: DEFAULT_PINCODE_REGEX,
  DE: DEFAULT_PINCODE_REGEX,
  JP: /^\d{3}-\d{4}$/,
  AU: /^[0-9]{4}$/,
  NZ: /^[0-9]{4}$/,
  AE: DEFAULT_PINCODE_REGEX,
};

const URL_REGEX = /^((?:https?:\/\/|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’])|([a-z0-9.\-]+[.][a-z]{2,4}))$/i;

const isEmpty = (value: string): boolean => value.trim() === '';

const hasSpecialCharacters = (value: string): boolean => SPECIAL_CHARACTERS_REGEX.test(value);

const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value.trim());

export const isValidPhoneNumber = (value: string, country: Countries): boolean =>
  isValidNumberForRegion(value, country);

const isValidUrl = (value: string): boolean => URL_REGEX.test(value.trim());

const isValidPincode = (value: string, country: Countries): boolean => {
  // https://jira.groupondev.com/browse/MIA-57
  if (country === 'IE') {
    return true;
  }
  const pincode = value.trim();
  if (PINCODE_REGEXES[country]) {
    return PINCODE_REGEXES[country].test(pincode);
  }
  return DEFAULT_PINCODE_REGEX.test(pincode);
};

const validate = (values: IFormSubmitValues) => {
  const errors: ValidationErrors = {};
  if (isEmpty(values.businessName)) {
    errors.businessName = VALIDATION_CONSTANTS.required;
  }
  if (!isValidPincode(values.postal_code, values.country)) {
    errors.zipCode =
      values.country === 'US'
        ? VALIDATION_CONSTANTS.zipCodeFiveDigits
        : VALIDATION_CONSTANTS.zipCode;
  }

  if (!isValidEmail(values.email)) {
    errors.email = VALIDATION_CONSTANTS.email;
  }
  if (!isValidPhoneNumber(values.phone, values.country)) {
    errors.phone = VALIDATION_CONSTANTS.phone;
  }
  if (isEmpty(values.city)) {
    errors.city = VALIDATION_CONSTANTS.required;
  }
  if (hasSpecialCharacters(values.city)) {
    errors.city = VALIDATION_CONSTANTS.specialCharacters;
  }

  const fullNameError = validateFullName(values.firstName, values.lastName);
  if (fullNameError) {
    errors.fullName = fullNameError;
  }

  // https://jira.groupondev.com/browse/MIA-136
  if (I18n.country === 'US' && !isValidUrl(values.website)) {
    errors.website = VALIDATION_CONSTANTS.url;
  }

  if (I18n.country !== 'US' && values.website && !isValidUrl(values.website)) {
    errors.website = VALIDATION_CONSTANTS.url;
  }

  if (isEmpty(values.street)) {
    errors.streetAddress = VALIDATION_CONSTANTS.required;
  }
  if (isEmpty(values.physicalLocations)) {
    errors.physicalLocations = VALIDATION_CONSTANTS.required;
  }
  if (values.typeOfBusiness[0] === '' || values.typeOfBusiness.length <= 0) {
    errors.typeOfBusiness = VALIDATION_CONSTANTS.pdsError;
  }
  if (values.country === 'US' && isEmpty(values.categoryPds)) {
    errors.categoryPds = VALIDATION_CONSTANTS.selectOfBusiness;
  }
  return errors;
};

export default validate;
